import { useCallback } from "react";
import { SystemCode } from "utils/engine/consts";
import { sanitizeBoilerTypeArray } from "utils/helpers/boilerType";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import { EtudeFormFields } from "../constants";
import { useDefaultFilters } from "./useDefaultFilters";
import { InstallationType } from "./useGetSolutionsQuery";

export const useGetSolutionRequestBody = (
  hasEcs?: boolean,
  hasChauffage?: boolean,
  calculatedSolutions?: EtudeSolution | null
) => {
  const { network } = useSalesNetwork();
  const { language } = useTranslation();

  const { getDefaultFilters, codeGammePacDefault } = useDefaultFilters(
    network?.code
  );

  const getRequestBody = useCallback(
    (values: EtudeFormFields, batiment: any) => {
      if (network && language) {
        const installationType: InstallationType =
          values.installationType as InstallationType;
        const traitement_anti_corrosion = values.antiCorrosion === "yes";
        const filters = getDefaultFilters(installationType);
        const requestBody: GetSolutionRequest = {
          code_localisation: language,
          code_reseau_commercial: network.code,
          code_gamme_pac: codeGammePacDefault,
          ville: values.city,
          departement: values.departement,
          altitude: values.altitude,
          t_base: values.t_base,
          traitement_anti_corrosion,
          distance_pac_ballon: values.distance_pac_ballon,
          distance_pac_batiment: values.distance_pac_batiment,
          hauteur_porte: values.hauteur_porte ?? undefined,
          largeur_porte: values.largeur_porte ?? undefined,
          ...(hasChauffage ? { debut_nc: values.fin_nc } : {}),
          ...(hasChauffage ? { fin_nc: values.debut_nc } : {}),
          ...(values.TcoupureQuestion ? { t_coupure: values.Tcoupure } : {}),
          //?
          ...(installationType === InstallationType.electric
            ? {
              t_ete: values.t_ete,
              bord_de_mer_montagne: (
                values.bdm_montagne_defaut === "yes"
              ).toString(),
              ...(values.systeme === SystemCode.ECHANGEUR_BALLONS_ECS && {
                moins_d_appoints: traitement_anti_corrosion.toString(),
              }),
            }
            : {
              ecs_sur_pac: hasChauffage && hasEcs ? values.boilerForEcs === "yes" : true,
            }),
          ...(hasEcs && {
            circuit_ecs: {
              batiment,
              t_eau_chaude: values.TECS,
              pertes_thermiques_bouclage: values.Pboucle + "",
              code_ballon_prep:
                calculatedSolutions?.references_commerciales?.filter((pr) =>
                  values.systeme === SystemCode.ECHANGEUR_BALLONS_ECS
                    ? pr.type === "exchange"
                    : pr.type === "stockprep"
                )[0]?.code,
              codes_ballon_stockage_ecs:
                calculatedSolutions?.references_commerciales
                  ?.filter((pr) => pr.type === "stockecs")
                  .map((pr) => pr.code),
              temperature_primaire: values.Tp,
              mode: "SI/SA", //values.mode,
              systeme: values.systeme,
              puissance_optimisee: calculatedSolutions?.puissance_optimisee,
              puissance_reelle: calculatedSolutions?.puissance_reelle,
              ...(network.displayInpLegionellaCycle &&
                values.antiLegionellaSupport === "yes"
                ? { cycle_al: values.antiLegionella }
                : {}),
            },
          }),
          ...(hasChauffage && {
            circuits: values.circuits
              ?.filter((c) => c.id)
              .map((c) => ({
                type: c.type,
                puissance: c.puissance,
                consigne_depart: c.startTemperature,
                delta_t: c.delta_t,
                surpuissance: c.surpuissance,
                ...(installationType === InstallationType.electric
                  ? {}
                  : {
                    plancher_chauffant: c.underfloorHeating === "yes",
                  }),
              })),
          }),
          filtres: {
            ...filters,
            ...(installationType === InstallationType.electric
              ? {}
              : {
                type_chaudiere: sanitizeBoilerTypeArray(filters.type_chaudiere),
              }),
          }

        };
        return requestBody;
      }
    },
    [
      network,
      language,
      hasEcs,
      hasChauffage,
      calculatedSolutions,
      getDefaultFilters,
      codeGammePacDefault,
    ]
  );

  return getRequestBody;
};
